import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  CheckCircle2, AlertTriangle, XCircle, RefreshCw, Activity, Loader2, Play, UserMinus, Trophy, Send, Search,
} from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Admin "System Health" dashboard.
 * Runs ~12 server-side checks and shows green/yellow/red badges.
 * Use this BEFORE asking the dev: most problems show up here first.
 */
const StatusIcon = ({ status }) => {
  if (status === 'ok') return <CheckCircle2 className="text-emerald-500 shrink-0" size={20} />;
  if (status === 'warn') return <AlertTriangle className="text-amber-500 shrink-0" size={20} />;
  return <XCircle className="text-red-500 shrink-0" size={20} />;
};

const STATUS_RANK = { fail: 0, warn: 1, ok: 2 };

const AdminSystemHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [runningAutomations, setRunningAutomations] = useState(false);
  const [cleaningTrials, setCleaningTrials] = useState(false);
  const [previewingDigest, setPreviewingDigest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [taskHealth, setTaskHealth] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  const fetchHealth = useCallback(async ({ silent } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const [res, taskRes] = await Promise.all([
        apiClient.get('/admin/system-health'),
        apiClient.get('/admin/background-task-health').catch(() => ({ data: null })),
      ]);
      setData(res.data);
      setTaskHealth(taskRes?.data || null);
    } catch (err) {
      toast.error('Failed to load system health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const reconcileStreaksNow = async () => {
    setReconciling(true);
    try {
      const res = await apiClient.post('/admin/background-tasks/reconcile-streaks-now');
      toast.success(`Streaks rebuilt for ${res.data.users_updated} users`);
      await fetchHealth({ silent: true });
    } catch (err) {
      toast.error('Failed to reconcile streaks');
    } finally {
      setReconciling(false);
    }
  };

  const runAutomationsNow = async () => {
    setRunningAutomations(true);
    try {
      const res = await apiClient.post('/automations/run');
      const sent = res.data?.notifications_sent ?? 0;
      const emails = res.data?.emails_queued ?? 0;
      const trials = res.data?.expired_trials_deactivated ?? 0;
      toast.success(`Automations ran: ${sent} notification${sent === 1 ? '' : 's'}, ${emails} email${emails === 1 ? '' : 's'}, ${trials} expired trial${trials === 1 ? '' : 's'} cleaned`);
      await fetchHealth({ silent: true });
    } catch (err) {
      toast.error('Failed to run automations');
    } finally {
      setRunningAutomations(false);
    }
  };

  const cleanupExpiredTrials = async () => {
    setCleaningTrials(true);
    try {
      const res = await apiClient.post('/admin/cleanup/expired-trials');
      toast.success(res.data?.message || 'Trial cleanup complete');
      await fetchHealth({ silent: true });
    } catch (err) {
      toast.error('Failed to cleanup expired trials');
    } finally {
      setCleaningTrials(false);
    }
  };

  const previewChampionsDigest = async () => {
    setPreviewingDigest(true);
    setDigestPreview(null);
    try {
      const res = await apiClient.post('/admin/champions-digest/preview');
      setDigestPreview(res.data);
    } catch (err) {
      toast.error('Failed to load digest preview');
    } finally {
      setPreviewingDigest(false);
    }
  };

  const sendChampionsDigestNow = async () => {
    if (!window.confirm("Send the 'Last Week's Champions' digest to every active member right now?")) return;
    setSendingDigest(true);
    try {
      const res = await apiClient.post('/admin/champions-digest/send-now');
      if (res.data?.status === 'skipped') {
        toast.info("Skipped — no positive milestones in the last 7 days.");
      } else {
        toast.success(`Digest sent: ${res.data?.sent || 0} emails, ${res.data?.total_milestones || 0} milestones celebrated.`);
      }
      setDigestPreview(null);
    } catch (err) {
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  return (
    <Layout>
      <div className="min-h-screen bg-[#09090b] p-4 sm:p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 flex items-center gap-3" data-testid="health-heading">
                <Activity className="text-[#EFB342]" /> System Health
              </h1>
              <p className="text-sm text-stone-600 mt-1">
                Live snapshot of automations, push notifications, and data integrity. Refreshes on demand.
              </p>
            </div>
            <Button
              onClick={() => fetchHealth({ silent: true })}
              disabled={refreshing || loading}
              variant="outline"
              className="border-stone-300 text-stone-700 hover:text-stone-900 shrink-0"
              data-testid="health-refresh-btn"
            >
              {refreshing ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              Refresh
            </Button>
          </div>

          {loading && !data && (
            <div className="text-stone-500 py-12 text-center">
              <Loader2 className="animate-spin inline mr-2" /> Running checks…
            </div>
          )}

          {data && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-6" data-testid="health-summary">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-400">{data.summary.ok}</p>
                  <p className="text-xs uppercase tracking-wide text-emerald-300/70 mt-1">Healthy</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-amber-400">{data.summary.warn}</p>
                  <p className="text-xs uppercase tracking-wide text-amber-300/70 mt-1">Warning</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-red-400">{data.summary.fail}</p>
                  <p className="text-xs uppercase tracking-wide text-red-300/70 mt-1">Failing</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="health-actions">
                <Button
                  onClick={runAutomationsNow}
                  disabled={runningAutomations || cleaningTrials}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30] flex-1"
                  data-testid="health-run-automations-btn"
                >
                  {runningAutomations ? <Loader2 className="animate-spin mr-2" size={16} /> : <Play size={16} className="mr-2" />}
                  Run Automations Now
                </Button>
                <Button
                  onClick={cleanupExpiredTrials}
                  disabled={runningAutomations || cleaningTrials}
                  variant="outline"
                  className="border-stone-300 text-stone-700 hover:text-stone-900 flex-1"
                  data-testid="health-cleanup-trials-btn"
                >
                  {cleaningTrials ? <Loader2 className="animate-spin mr-2" size={16} /> : <UserMinus size={16} className="mr-2" />}
                  Deactivate Expired Trials
                </Button>
                <Link to="/admin/automation-debug" className="flex-1">
                  <Button
                    variant="outline"
                    className="border-stone-300 text-stone-700 hover:text-stone-900 w-full"
                    data-testid="health-diagnose-user-btn"
                    title="Look up why an automation didn't fire for a specific user"
                  >
                    <Search size={16} className="mr-2" />
                    Diagnose a user
                  </Button>
                </Link>
              </div>

              {/* Background tasks health */}
              {taskHealth && (
                <div className="bg-white/60 border border-stone-200 rounded-2xl p-5 mb-4" data-testid="background-tasks-health">
                  <div className="flex items-baseline justify-between mb-4">
                    <div>
                      <h3 className="text-stone-900 font-semibold flex items-center gap-2">
                        <Activity size={16} className="text-[#EFB342]" />
                        Background Tasks
                      </h3>
                      <p className="text-xs text-stone-500 mt-1">
                        Silent failures used to cause missed automations. These heartbeats show each task is actually running.
                      </p>
                    </div>
                    <Button
                      onClick={reconcileStreaksNow}
                      disabled={reconciling}
                      size="sm"
                      variant="outline"
                      className="border-stone-300 text-stone-700 hover:text-stone-900 text-xs"
                      data-testid="reconcile-streaks-btn"
                    >
                      {reconciling ? <Loader2 className="animate-spin mr-1" size={12} /> : <RefreshCw size={12} className="mr-1" />}
                      Rebuild Streaks
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {['scheduled_notifications', 'inactivity_automations', 'streak_reconciliation'].map((taskName) => {
                      const t = (taskHealth.tasks || []).find((x) => x.task === taskName);
                      const label = {
                        scheduled_notifications: 'Scheduled Notifications',
                        inactivity_automations: 'Inactivity / Nudges',
                        streak_reconciliation: 'Streak Reconciliation',
                      }[taskName];
                      const statusStyle = {
                        green:  { box: 'bg-emerald-500/10 border-emerald-500/30', text: 'text-emerald-300', dot: '● Healthy' },
                        yellow: { box: 'bg-amber-500/10 border-amber-500/30',    text: 'text-amber-300',   dot: '● Slow / stale' },
                        red:    { box: 'bg-red-500/10 border-red-500/30',        text: 'text-red-300',     dot: '● Not running!' },
                        none:   { box: 'bg-stone-100/40 border-stone-300',          text: 'text-stone-600',    dot: '● Never run' },
                      };
                      const key = t?.status === 'green' || t?.status === 'yellow' || t?.status === 'red' ? t.status : 'none';
                      const s = statusStyle[key];
                      const ageLabel = t?.age_minutes == null
                        ? 'never run'
                        : t.age_minutes < 60
                          ? `${t.age_minutes}m ago`
                          : `${Math.floor(t.age_minutes / 60)}h ago`;
                      return (
                        <div key={taskName} className={`rounded-xl p-3 border ${s.box}`} data-testid={`task-health-${taskName}`}>
                          <p className="text-stone-900 text-sm font-medium">{label}</p>
                          <p className={`text-xs mt-1 ${s.text}`}>{s.dot}</p>
                          <p className="text-xs text-stone-500 mt-1">Last: {ageLabel}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Last Week's Champions digest controls */}
              <div className="bg-white border border-stone-200 rounded-xl p-4 mb-6" data-testid="champions-digest-panel">
                <div className="flex items-start gap-3 mb-3">
                  <Trophy className="text-[#EFB342] shrink-0 mt-0.5" size={20} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-stone-900">Last Week&apos;s Champions Digest</p>
                    <p className="text-xs text-stone-600 mt-0.5">
                      Fires automatically every <strong className="text-stone-700">Monday at 8am PST</strong>. Sends a
                      celebration email listing every positive milestone from the past 7 days to all active members.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={previewChampionsDigest}
                    disabled={previewingDigest || sendingDigest}
                    variant="outline"
                    className="border-stone-300 text-stone-700 hover:text-stone-900 flex-1"
                    data-testid="digest-preview-btn"
                  >
                    {previewingDigest ? <Loader2 className="animate-spin mr-2" size={16} /> : <Trophy size={16} className="mr-2" />}
                    Preview This Week
                  </Button>
                  <Button
                    onClick={sendChampionsDigestNow}
                    disabled={previewingDigest || sendingDigest}
                    className="bg-[#EFB342] text-black hover:bg-[#D99F30] flex-1"
                    data-testid="digest-send-now-btn"
                  >
                    {sendingDigest ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                    Send Now
                  </Button>
                </div>
                {digestPreview && (
                  <div className="mt-4 pt-4 border-t border-stone-200" data-testid="digest-preview-content">
                    {digestPreview.status === 'empty' ? (
                      <p className="text-sm text-amber-400">{digestPreview.message}</p>
                    ) : (
                      <>
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Subject</p>
                        <p className="text-sm text-stone-900 mb-3">{digestPreview.subject}</p>
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Would send to</p>
                        <p className="text-sm text-stone-900 mb-3">{digestPreview.would_send_to} active member{digestPreview.would_send_to === 1 ? '' : 's'} — celebrating {digestPreview.total_champions} champion{digestPreview.total_champions === 1 ? '' : 's'} ({digestPreview.total_milestones} milestone{digestPreview.total_milestones === 1 ? '' : 's'})</p>
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Body preview</p>
                        <div
                          className="bg-white/95 border border-stone-200 rounded-lg p-3 text-sm text-stone-800 max-h-[280px] overflow-y-auto"
                          // Trusted admin-generated preview HTML; safe to render.
                          dangerouslySetInnerHTML={{ __html: digestPreview.body_html }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2" data-testid="health-checks-list">
                {[...data.checks]
                  .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
                  .map((c, idx) => (
                    <div
                      key={`${c.check}-${idx}`}
                      className="bg-white border border-stone-200 rounded-xl px-4 py-3 flex items-start gap-3"
                      data-testid={`health-check-${c.status}`}
                    >
                      <StatusIcon status={c.status} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-900">{c.check}</p>
                        <p className="text-xs text-stone-600 mt-0.5">{c.detail}</p>
                      </div>
                    </div>
                  ))}
              </div>

              <p className="text-xs text-stone-400 mt-6 text-center">
                Snapshot generated {new Date(data.generated_at).toLocaleString()}
              </p>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminSystemHealth;
